import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader, AlertCircle, MessageCircle, Mail, MessageSquare } from 'lucide-react';
import { chatApi } from '../../services/chatApi';

const MessageComposer = ({ 
  conversationId, 
  channel, 
  onMessageSent, 
  disabled = false,
  className = "" 
}) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  // Channel-specific configurations
  const channelConfig = {
    sms: {
      icon: MessageCircle,
      placeholder: 'Type your SMS message...',
      maxLength: 1600,
      color: 'blue'
    },
    email: {
      icon: Mail,
      placeholder: 'Type your email message...',
      maxLength: 10000,
      color: 'green'
    },
    webchat: {
      icon: MessageSquare,
      placeholder: 'Type your chat message...',
      maxLength: 2000,
      color: 'purple'
    }
  };

  const config = channelConfig[channel] || channelConfig.sms;
  const Icon = config.icon;

  const handleSend = async () => {
    if (!message.trim() || sending) return;

    try {
      setSending(true);
      setError(null);

      const result = await chatApi.sendManualMessage(
        conversationId, 
        message.trim(),
        'Support Team'
      );



      // Clear the message
      setMessage('');

      // Notify parent component
      if (onMessageSent) {
        onMessageSent(result);
      }

    } catch (error) {
      console.error('Failed to send message:', error);
      setError(error.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isValidMessage = message.trim().length > 0 && message.length <= config.maxLength;
  const charactersUsed = message.length;
  const charactersRemaining = config.maxLength - charactersUsed;
  const isNearLimit = charactersRemaining < 100;

  return (
    <div className={`bg-white border-t border-gray-200 ${className}`}>
      {error && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-100">
          <div className="flex items-center text-red-600 text-sm">
            <AlertCircle className="w-4 h-4 mr-2" />
            {error}
          </div>
        </div>
      )}

      <div className="p-4">
        <div className="flex items-stretch gap-3">
          {/* Message Input and Send Button on same line */}
          <div className="flex-1 min-w-0">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={config.placeholder}
              disabled={disabled || sending}
              className="w-full p-3 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[44px] max-h-32 transition-all duration-200"
              rows="1"
            />
          </div>

          {/* Send Button */}
          <button
            onClick={handleSend}
            disabled={!isValidMessage || sending || disabled}
            className={`flex items-center justify-center gap-2 px-4 rounded-lg text-sm font-medium transition-colors duration-200 flex-shrink-0 h-[44px] ${
              isValidMessage && !sending && !disabled
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {sending ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessageComposer; 